import type { ApiKeyInfo, ApiScope, CreateApiKeyResponse, DeckRole, DeckSummary, NotesResponse, RevisionInfo } from "@deckloom/core/protocol";
import type { Annotation, Deck, LayersFile } from "@deckloom/core/schema";
import { validateDeck } from "@deckloom/core/schema";

/** role: what the `?p=` token is to the deck: its operator (it drives, draws, edits, holds the API keys), or a viewer */
export type DeckInfo = DeckSummary & { role: DeckRole };

async function check<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const bearer = (token: string, json = false): Record<string, string> => ({ authorization: `Bearer ${token}`, ...(json ? { "content-type": "application/json" } : {}) });

export const api = {
  deck: (deckId: string, token?: string | null) =>
    fetch(`/api/decks/${deckId}${token ? `?p=${encodeURIComponent(token)}` : ""}`).then(check<DeckInfo>),

  deckJson: async (deckId: string): Promise<Deck> => validateDeck(await fetch(`/api/decks/${deckId}/deck.json`).then(check<unknown>)),

  layers: (deckId: string) => fetch(`/api/decks/${deckId}/layers`).then(check<LayersFile>),

  mediaStatus: (deckId: string) =>
    fetch(`/api/decks/${deckId}/media-status`).then(check<{ media_id: string; kind: string; transcode_status: string; error: string | null }[]>),

  apiKeys: (deckId: string, token: string) => fetch(`/api/decks/${deckId}/keys`, { headers: bearer(token) }).then(check<ApiKeyInfo[]>),
  createApiKey: (deckId: string, token: string, name: string, scopes: ApiScope[]) =>
    fetch(`/api/decks/${deckId}/keys`, { method: "POST", headers: bearer(token, true), body: JSON.stringify({ name, scopes }) }).then(check<CreateApiKeyResponse>),
  revokeApiKey: async (deckId: string, token: string, keyId: string) => {
    const res = await fetch(`/api/decks/${deckId}/keys/${keyId}`, { method: "DELETE", headers: bearer(token) });
    if (!res.ok && res.status !== 404) throw new Error(`${res.status}`);
  },

  /* solo mode's drawings go to the deck's drawing layer through its API (the room shows them at once) */
  addDrawing: (deckId: string, token: string, page: string, drawing: Annotation) =>
    fetch(`/api/decks/${deckId}/pages/${encodeURIComponent(page)}/drawings`, { method: "POST", headers: bearer(token, true), body: JSON.stringify(drawing) }).then(check<unknown>),
  deleteDrawing: async (deckId: string, token: string, page: string, drawingId: string) => {
    const res = await fetch(`/api/decks/${deckId}/pages/${encodeURIComponent(page)}/drawings/${encodeURIComponent(drawingId)}`, { method: "DELETE", headers: bearer(token) });
    if (!res.ok && res.status !== 404) await check(res);
  },
  clearDrawings: (deckId: string, token: string, page: string) =>
    fetch(`/api/decks/${deckId}/pages/${encodeURIComponent(page)}/drawings`, { method: "DELETE", headers: bearer(token) }).then(check<void>),

  /* solo mode works on the working copy: the head's slides, the notes saved to it as an edit */
  deckJsonAt: async (deckId: string, revision: number): Promise<Deck> => validateDeck(await fetch(`/api/decks/${deckId}/revisions/${revision}/deck.json`).then(check<unknown>)),
  setNote: (deckId: string, token: string, page: string, text: string) =>
    fetch(`/api/decks/${deckId}/pages/${encodeURIComponent(page)}/notes`, { method: "PUT", headers: bearer(token, true), body: JSON.stringify({ text }) }).then(check<NotesResponse>),

  /* revisions of the slides: every edit saved; one applied is what the room shows; going back to one to work from */
  revisions: (deckId: string) => fetch(`/api/decks/${deckId}/revisions`).then(check<RevisionInfo[]>),
  applyRevision: (deckId: string, token: string, revision: number) =>
    fetch(`/api/decks/${deckId}/revisions/${revision}/apply`, { method: "POST", headers: bearer(token) }).then(check<{ revision: number }>),
  restore: (deckId: string, token: string, revision: number) =>
    fetch(`/api/decks/${deckId}/revisions/${revision}/restore`, { method: "POST", headers: bearer(token) }).then(check<{ revision: number; restored: number }>),

  mediaUrl: (deckId: string) => (mediaId: string) => `/api/decks/${deckId}/media/${mediaId}`,
  playUrl: (deckId: string) => (mediaId: string) => `/api/decks/${deckId}/media/${mediaId}/play`,
};
